'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';

import { useSvgChartDimensions } from '../chart/use-svg-chart-dimensions';
import { useIsTouchDevice } from './use-is-touch-device';

export interface ChartDataPoint {
  date: string;
  value: number;
}

interface TrendChartProps {
  data: ChartDataPoint[];
  width?: number;
  height?: number;
  onHover?: (point: ChartDataPoint | null) => void;
  formatYAxisLabel?: (value: number) => string;
}

const PADDING_TOP = 28;
const PADDING_BOTTOM = 24;
const PADDING_LEFT_BASE = 40;
const PADDING_LEFT_WIDE = 64;
const PADDING_RIGHT = 16;
const Y_TICK_COUNT = 5;
const BEZIER_SHARPNESS = 3;
const BADGE_W = 56;
const BADGE_H = 18;

export const TrendChart = ({ data, width, height, onHover, formatYAxisLabel }: TrendChartProps) => {
  const containerRef = useRef<SVGSVGElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const isTouchDevice = useIsTouchDevice();
  const dimensions = useSvgChartDimensions(containerRef, width, height);

  const activeIndex = hoveredIndex ?? data.length - 1;

  const { min, max, yRange } = useMemo(() => {
    if (!data.length) return { min: 0, max: 0, yRange: 0.1 };
    const values = data.map((d) => d.value);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    if (minVal === maxVal) {
      const range = Math.max(Math.abs(maxVal) * 0.01, 0.01);
      return { min: minVal - range / 2, max: maxVal + range / 2, yRange: range };
    }
    const range = maxVal - minVal;
    return { min: minVal, max: maxVal, yRange: range };
  }, [data]);

  const paddingLeft = yRange < 0.01 ? PADDING_LEFT_WIDE : PADDING_LEFT_BASE;

  const yTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let i = 0; i < Y_TICK_COUNT; i++) {
      ticks.push(max - (yRange * i) / (Y_TICK_COUNT - 1));
    }
    return ticks;
  }, [max, yRange]);

  const chartArea = useMemo(
    () => ({
      left: paddingLeft,
      right: dimensions.width - PADDING_RIGHT,
      top: PADDING_TOP,
      bottom: dimensions.height - PADDING_BOTTOM,
      width: Math.max(0, dimensions.width - paddingLeft - PADDING_RIGHT),
      height: Math.max(0, dimensions.height - PADDING_TOP - PADDING_BOTTOM),
    }),
    [dimensions, paddingLeft],
  );

  const chartData = useMemo(() => {
    if (!data?.length || chartArea.width <= 0 || chartArea.height <= 0) return [];

    const xScale = chartArea.width / Math.max(1, data.length - 1);

    return data.map((point, i) => {
      const x = chartArea.left + i * xScale;
      const normalizedValue = (point.value - min) / yRange;
      const y = chartArea.top + chartArea.height * (1 - normalizedValue);
      return {
        x,
        y: Math.max(chartArea.top, Math.min(chartArea.bottom, y)),
        ...point,
      };
    });
  }, [data, chartArea, min, yRange]);

  const pathData = useMemo(() => {
    if (!chartData.length) return '';

    let path = `M ${chartData[0].x},${chartData[0].y}`;
    for (let i = 0; i < chartData.length - 1; i++) {
      const current = chartData[i];
      const next = chartData[i + 1];
      const offset = (next.x - current.x) / BEZIER_SHARPNESS;
      path += ` C ${current.x + offset},${current.y} ${next.x - offset},${next.y} ${next.x},${next.y}`;
    }
    return path;
  }, [chartData]);

  const activePoint = chartData[activeIndex];

  const handleTouch = useCallback(
    (e: React.TouchEvent) => {
      const svg = containerRef.current;
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;

      const pointWidth = chartArea.width / (data.length - 1);
      const index = Math.round((touchX - chartArea.left) / pointWidth);
      if (index >= 0 && index < data.length) {
        setHoveredIndex(index);
      }
    },
    [data.length, chartArea],
  );

  useEffect(() => {
    const point = hoveredIndex !== null ? data[hoveredIndex] : data[data.length - 1];
    onHover?.(point || null);
  }, [hoveredIndex, data, onHover]);

  if (!chartData.length || !activePoint) {
    return <svg ref={containerRef} width={width || '100%'} height={height || '100%'} />;
  }

  const activeDateLabel = formatDate(data[activeIndex].date, true);
  const firstDateLabel = formatDate(data[0].date, false);
  const lastDateLabel = formatDate(data[data.length - 1].date, false);
  const badgeX = Math.max(chartArea.left, Math.min(activePoint.x - BADGE_W / 2, chartArea.right - BADGE_W));
  const badgeY = chartArea.top - BADGE_H - 4;

  return (
    <svg
      ref={containerRef}
      width={width || '100%'}
      height={height || '100%'}
      style={{ display: 'block', overflow: 'visible', touchAction: isTouchDevice ? 'none' : undefined }}
      onMouseLeave={
        !isTouchDevice
          ? () => {
              setHoveredIndex(null);
              onHover?.(data[data.length - 1] || null);
            }
          : undefined
      }
      onTouchStart={isTouchDevice ? handleTouch : undefined}
      onTouchMove={isTouchDevice ? handleTouch : undefined}
    >
      {yTicks.map((tick, i) => {
        const y = chartArea.top + chartArea.height * (1 - (tick - min) / yRange);
        return (
          <g key={`y-${i}`}>
            <line
              x1={chartArea.left}
              y1={y}
              x2={chartArea.right}
              y2={y}
              stroke={token('colors.border.default.level-1')}
              strokeWidth={1}
              strokeDasharray='3 3'
            />
            <text
              x={paddingLeft - 8}
              y={y + 4}
              textAnchor='end'
              fill={token('colors.content.default.level-1')}
              className={css({ textStyle: 'Text/XS/Regular' })}
            >
              {formatYAxisLabel
                ? formatYAxisLabel(tick)
                : tick.toFixed(yRange < 0.001 ? 6 : yRange < 0.01 ? 4 : yRange < 0.1 ? 3 : 2)}
            </text>
          </g>
        );
      })}

      <path d={pathData} fill='none' stroke='#ffffff' strokeWidth={3} strokeLinecap='round' />

      <line
        x1={activePoint.x}
        y1={chartArea.top}
        x2={activePoint.x}
        y2={chartArea.bottom}
        stroke={token('colors.border.default.level-4')}
        strokeWidth={1}
        strokeDasharray='2 2'
      />

      <g>
        {activeIndex === data.length - 1 && (
          <circle cx={activePoint.x} cy={activePoint.y} r={10} fill='white' className='trend-chart-pulse' />
        )}
        <circle
          cx={activePoint.x}
          cy={activePoint.y}
          r={3.5}
          fill={token('colors.background.default.level-0')}
          stroke='white'
          strokeWidth={2.5}
        />
      </g>

      <g>
        <rect
          x={badgeX}
          y={badgeY}
          width={BADGE_W}
          height={BADGE_H}
          rx={9}
          fill={token('colors.background.default.level-4')}
        />
        <text
          x={badgeX + BADGE_W / 2}
          y={badgeY + BADGE_H / 2 + 4}
          textAnchor='middle'
          fill={token('colors.content.default.level-5')}
          className={css({ textStyle: 'Text/XS/Monospace/Regular' })}
        >
          {activeDateLabel}
        </text>
      </g>

      <text
        x={chartArea.left}
        y={dimensions.height - 4}
        textAnchor='start'
        fill={token('colors.content.default.level-1')}
        className={css({ textStyle: 'Text/XS/Regular' })}
      >
        {firstDateLabel}
      </text>
      <text
        x={chartArea.right}
        y={dimensions.height - 4}
        textAnchor='end'
        fill={token('colors.content.default.level-1')}
        className={css({ textStyle: 'Text/XS/Regular' })}
      >
        {lastDateLabel}
      </text>

      {chartData.map((point, i) => (
        <rect
          key={i}
          x={point.x - chartArea.width / data.length / 2}
          y={chartArea.top}
          width={Math.max(0, chartArea.width / data.length)}
          height={Math.max(0, chartArea.height)}
          onMouseEnter={!isTouchDevice ? () => setHoveredIndex(i) : undefined}
          fill='transparent'
        />
      ))}

      <style>
        {`
          @keyframes trendChartPulseAnimation {
            0% { opacity: 0; }
            50% { opacity: 0.3; }
            100% { opacity: 0; }
          }
          .trend-chart-pulse {
            animation: trendChartPulseAnimation 1s ease-in-out infinite;
          }
        `}
      </style>
    </svg>
  );
};

/** @param short - true for badge format (YY.MM.DD), false for axis (YYYY.MM.DD) */
function formatDate(dateStr: string, short: boolean): string {
  const parts = dateStr.split('-');
  const year = short ? parts[0].slice(2) : parts[0];
  return `${year}.${parts[1]}.${parts[2]}`;
}
